import { lazy, Suspense, useEffect, useState, useRef } from "react";

const StickerCarouselSection = lazy(() =>
  import("./StickerCarouselSection").then(m => ({ default: m.StickerCarouselSection }))
);

/** Skeleton placeholder matching carousel dimensions to prevent CLS */
function CarouselSkeleton() {
  return (
    <div className="max-w-6xl mx-auto px-4" aria-hidden="true">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {[1, 2, 3, 4].map(i => (
          <div
            key={i}
            className="bg-background rounded-xl shadow-md p-4 flex flex-col items-center justify-between animate-pulse"
            style={{ height: "340px" }}
          >
            <div className="w-full bg-muted rounded-lg" style={{ height: "210px" }} />
            <div className="mt-4 w-full space-y-2">
              <div className="h-4 bg-muted rounded w-3/4 mx-auto" />
              <div className="h-3 bg-muted rounded w-1/2 mx-auto" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function LazyCarouselSection() {
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !isInView) {
          setIsInView(true);
        }
      },
      {
        rootMargin: "200px",
        threshold: 0.01,
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [isInView]);

  return (
    <section ref={sectionRef} className="py-16 bg-muted/30">
      <div className="container mx-auto px-4 mb-10 text-center">
        <h2 className="text-h2 md:text-h1 font-bold text-foreground mb-3">AI-Generated Designs</h2>
        <p className="text-lg text-muted-foreground">
          See what&apos;s possible with our AI design tool
        </p>
      </div>

      {isInView ? (
        <Suspense fallback={<CarouselSkeleton />}>
          <StickerCarouselSection />
        </Suspense>
      ) : (
        <CarouselSkeleton />
      )}
    </section>
  );
}
